import * as R from "ramda";
import * as Rx from "rxjs";
import { Scheduler } from "rxjs/Scheduler";

import { PliCallResponse, PliCallErrorData, PliCallSettings } from "../core/call";
import { PliCallRxSettings, fromPliCall } from "../util/rx";

import { DataProviderRequest, DataProviderResponse, DataProviderRequestHandler } from "./dataProvider";

/**
 * Settings for getSearchStream.
 */
export interface DebouncedStreamSettings {

    /**
     * Number of milliseconds to use when debouncing the search stream (optional).
     *
     * A negative value or a value of zero will result in no debouncing.
     *
     * @default 250
     */
    debounceTime?: number;

    /**
     * The Rx scheduler to use for debounced streams (optional).
     */
    scheduler?: Scheduler;

}

/**
 * Default number of milliseconds to throttle a search request.
 */
export const defaultDebounceTime = 250;

/**
 * Transforms and debounces a stream of incoming requests into responses.
 *
 * @param requestStream
 *     Observable stream of user request objects.
 * @param queryProvider
 *     Function that takes a request and produces an observable stream of responses.
 * @param settings
 *     Settings controlling stream behaviour.
 * @returns
 *     Observable stream of responses corresponding to the incoming requests.
 */
export function getDebouncedResponsesFor<TRequest, TResponse>(
    requestStream: Rx.Observable<TRequest>,
    queryProvider: (r: TRequest) => Rx.Observable<TResponse>,
    settings: DebouncedStreamSettings = {})
        : Rx.Observable<TResponse>
{
    if (!requestStream) { throw new Error("Expected 'requestStream' to be an Rx Observable."); }
    if (typeof queryProvider !== "function") { throw new Error("Expected 'queryProvider' to be a function."); }

    const debounceTime = settings.debounceTime === undefined
        ? defaultDebounceTime
        : settings.debounceTime;

    const debouncedStream = debounceTime <= 0
        ? requestStream
        : requestStream.debounceTime(debounceTime, settings.scheduler);

    return debouncedStream
        .map(queryProvider)
        .switch();
}

/**
 * Converts a (possibly erroneous) PliCallResponse to a DataProviderResponse.
 */
function toDataProviderResponse(response: PliCallResponse<DataProviderResponse, any>): DataProviderResponse {
    return response.isError
        ? { data: null, error: (<PliCallErrorData>response.result).message }
        : response.result as DataProviderResponse;
}

/**
 * Creates a DataProviderRequestHandler for a PLI call.
 *
 * Call settings will be passed into "fromPliCall". Values
 * for "data" (or "jsonData") will be merged with the incoming
 * request. Errors will be included in the result stream with
 * "null" data and the error message passed from the server (if any).
 *
 * @param settings
 *     PliCallSettings to use for each request.
 * @returns
 *     DataProviderRequestHandler function that produces an Observable
 *     stream of DataProviderResponse values for each request.
 */
export function pliCallRequestHandler(settings: PliCallSettings<any, any>): DataProviderRequestHandler {
    const rxSettings: PliCallRxSettings<DataProviderRequest> = {
        customSignIn: settings.customSignIn,
        deferSignInUiClosingUntil: settings.deferSignInUiClosingUntil,
        headers: settings.headers,
        includeErrorsInResultStream: true,
        ignoreUnauthenticated: settings.ignoreUnauthenticated,
        name: settings.name,
        owner: settings.owner
    };

    const data = settings.data || (settings.jsonData && JSON.parse(settings.jsonData)) || undefined;
    const injectDataTo = (data ? R.merge(data) : R.identity);

    return request => {
        rxSettings.data = injectDataTo(request);
        return fromPliCall<DataProviderResponse>(rxSettings).map(toDataProviderResponse);
    };
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/data-provider/shared.ts